import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { GoogleIcon } from "../../components/ui/google-icon";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, firebaseService, clearUserSessionData } from "../../lib/firebase";
import { useSystemSettings } from "../../lib/SystemSettingsContext";
import { useToast } from "../../components/ui/use-toast";

export const SignInPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { settings } = useSystemSettings();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Check if login is disabled
  useEffect(() => {
    if (settings.disableLogin) {
      setError("Login is currently disabled by the administrator.");
    }
  }, [settings.disableLogin]);

  // Clear any existing session data when SignInPage loads
  useEffect(() => {
    // Clear any existing session data to ensure a clean login experience
    clearUserSessionData();
  }, []);

  useEffect(() => {
    // Check if Firebase is initialized
    if (!firebaseService.isInitialized || !auth) {
      console.error("Firebase not initialized");
      setError("Authentication service is not available. Please try again later.");
      return;
    }

    // Check if user is already signed in
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.uid) {
        console.log("User is already signed in:", user.email);
        // Only redirect if user is actually authenticated (not just cached data)
        if (user.emailVerified !== false) { // emailVerified can be null for some providers
          navigate("/dashboard");
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Check if login is disabled
    if (settings.disableLogin) {
      setError("Login is currently disabled by the administrator.");
      return;
    }
    
    setLoading(true);

    if (!firebaseService.isInitialized || !auth) {
      setError("Firebase authentication is not available at the moment. Please try again later.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      // Sync to Firestore
      await setDoc(doc(db, "users", user.uid), {
        email: user.email?.toLowerCase() || "",
        name: user.displayName || user.email,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      toast({
        title: "Welcome back!",
        description: `Signed in as ${formData.email}`,
      });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    
    // Check if login is disabled
    if (settings.disableLogin) {
      setError("Login is currently disabled by the administrator.");
      return;
    }
    
    setLoading(true);

    if (!firebaseService.isInitialized || !auth) {
      setError("Firebase authentication is not available at the moment. Please try again later.");
      setLoading(false);
      return;
    }

    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      // Sync to Firestore
      await setDoc(doc(db, "users", user.uid), {
        email: user.email?.toLowerCase() || "",
        name: user.displayName || user.email,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      toast({
        title: "Welcome back!",
        description: `Signed in with Google`,
      });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <Button 
              className="w-[166px] h-[49px] bg-[#daed6e] rounded-[20px] border border-solid border-[#e7e7e7] hover:bg-[#c5d85c]"
              onClick={() => navigate('/join')}
              disabled={settings.disableSignup}
            >
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">JOIN</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 min-h-screen">
        <div className="w-full max-w-[480px] bg-white rounded-[20px] p-8 shadow-lg mt-[140px]">
          <div className="flex flex-col items-center mb-8">
            <img className="w-16 h-16 mb-4" alt="Beetle" src="/beetle.png" />
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Welcome back!
            </h1>
          </div>
          
          <form className="space-y-4" onSubmit={handleLogin}>
            {error && (
              <div className="p-3 text-sm text-red-500 bg-red-50 rounded-lg">
                {error}
              </div>
            )}
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className="h-12 bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-base focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
              placeholder="Email"
              required
              disabled={settings.disableLogin}
            />
            <Input
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              className="h-12 bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-base focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
              placeholder="Password"
              required
              disabled={settings.disableLogin}
            />

            <Button 
              type="submit"
              disabled={loading || settings.disableLogin}
              className="w-full h-12 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                {loading ? 'Signing in...' : 'Log in'}
              </span>
            </Button>

            <div className="text-center">
              <button 
                type="button" 
                className="text-sm text-black opacity-80 hover:underline"
                onClick={() => {/* TODO: Implement password reset */}}
                disabled={settings.disableLogin}
              >
                Forgot Password?
              </button>
            </div>

            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 h-px bg-[#E7E7E7]"></div>
              <span className="text-sm text-black opacity-50">OR</span>
              <div className="flex-1 h-px bg-[#E7E7E7]"></div>
            </div>

            <button 
              type="button" 
              disabled={loading || settings.disableLogin}
              className="w-full h-12 bg-white rounded-[100px] border border-[#E7E7E7] flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleGoogleSignIn}
            >
              <div className="flex items-center gap-2">
                <GoogleIcon />
                <span className="font-['Space_Grotesk'] text-base text-black">
                  {loading ? 'Signing in...' : 'Log in with Google'}
                </span>
              </div>
            </button>

            <div className="text-center mt-6">
              <span className="text-sm text-black opacity-80">Don't have an account? </span>
              <button 
                type="button" 
                className="text-sm text-black font-medium hover:underline"
                onClick={() => navigate('/join')}
                disabled={settings.disableSignup}
              >
                Sign up
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};